/**
 * resolveStoreData
 *
 * Resolves the data value(s) and setter(s) for a section from the available
 * state stores, driven entirely by sectionConfig fields.
 *
 * Each store entry shape: { data, setData, pathBased }
 *   pathBased = true  → setData(path, value)   (e.g. selectedSectionsData, medicalBackground)
 *   pathBased = false → setData(value)          (e.g. vitals, dentalData, bloodGroup)
 */

const makePathSetter = (store, path) => (value) => store.setData(path, value);

/**
 * Resolves a single section's data and setter.
 *
 * Returns:
 *   - Single binding  → { data, setData }
 *   - dataBindings[]  → { [key]: data, [setKey]: setter, ... }  (e.g. FollowUp + Referral)
 */
export const resolveStoreData = (sectionConfig, stores) => {
  const { dataStore, dataPath, dataBindings } = sectionConfig ?? {};

  if (dataBindings?.length) {
    return dataBindings.reduce((acc, binding) => {
      const store = stores[binding.store];
      if (!store) return acc;
      acc[binding.key] = binding.path ? store.data?.[binding.path] : store.data;
      acc[`set${binding.key}`] =
        binding.path && store.pathBased
          ? makePathSetter(store, binding.path)
          : store.setData;
      return acc;
    }, {});
  }

  if (!dataStore) return {};
  const store = stores[dataStore];
  if (!store) return {};

  const data = dataPath ? store.data?.[dataPath] : store.data;
  const setData =
    dataPath && store.pathBased ? makePathSetter(store, dataPath) : store.setData;

  return { data, setData };
};

/**
 * Builds the stores map from the component-level state props so that
 * resolveStoreData can look up any store by its config id.
 */
export const buildStores = ({
  selectedSectionsData,
  updateSectionData,
  medicalBackground,
  updateMedicalBackground,
  vitals,
  setVitals,
  dentalData,
  setDentalData,
  bloodGroup,
  setBloodGroup,
}) => ({
  selectedSectionsData: {
    data: selectedSectionsData,
    setData: updateSectionData,
    pathBased: true,
  },
  medicalBackground: {
    data: medicalBackground,
    setData: updateMedicalBackground,
    pathBased: true,
  },
  vitals: { data: vitals, setData: setVitals, pathBased: false },
  dentalData: { data: dentalData, setData: setDentalData, pathBased: false },
  bloodGroup: { data: bloodGroup, setData: setBloodGroup, pathBased: false },
});
